/**
 * Deterministischer Zufall für die Simulation.
 *
 * Kern ist sfc32 — schnell, klein, mit gutem Verhalten und einem Zustand aus
 * vier 32-Bit-Wörtern, der sich vollständig speichern und wiederherstellen lässt.
 * Genau das brauchen wir: Ein Spielstand muss den Zufallszustand mitnehmen, sonst
 * ist ein fortgesetzter Lauf nicht mehr reproduzierbar.
 *
 * WICHTIG — die Trennung in benannte Ströme ist keine Kosmetik:
 * Wenn eine Balance-Änderung die Anzahl der Krit-Würfe verschiebt, darf sich der
 * Beutestrom nicht mitverschieben. Sonst ist kein goldener Lauf vergleichbar und
 * keine Balance-Messung aussagekräftig. Siehe docs/2026-07-25-architektur.md §4.2.
 */

/** Zustand eines Stroms: vier vorzeichenlose 32-Bit-Wörter. */
export type StromZustand = readonly [number, number, number, number];

export interface Strom {
  /** Name des Stroms — nur für Diagnose. */
  readonly name: string;
  /** Gleitkommazahl in [0, 1). */
  zahl(): number;
  /** Ganzzahl in [min, max], beide Grenzen einschließlich. */
  ganz(min: number, max: number): number;
  /** Gleitkommazahl in [min, max). */
  spanne(min: number, max: number): number;
  /** true mit Wahrscheinlichkeit p (0..1). */
  chance(p: number): boolean;
  /** Ein Element aus einer nichtleeren Liste. */
  waehle<T>(liste: readonly T[]): T;
  /**
   * Gewichtete Auswahl. `gewichte[i]` gehört zu `liste[i]`.
   * Gewichte müssen ≥ 0 sein und in der Summe > 0.
   */
  gewichtet<T>(liste: readonly T[], gewichte: readonly number[]): T;
  /** Mischt eine Kopie der Liste (Fisher-Yates). Die Vorlage bleibt unberührt. */
  mische<T>(liste: readonly T[]): T[];
  /** Aktuellen Zustand auslesen (für Spielstand und goldene Läufe). */
  zustand(): StromZustand;
  /** Zustand setzen. */
  setzeZustand(z: StromZustand): void;
  /** Anzahl gezogener Werte — Diagnose, deckt Verschiebungen auf. */
  zaehler(): number;
}

/**
 * xmur3 — Streuung einer Zeichenkette auf 32 Bit.
 * Wird nur zur Ableitung von Startwerten benutzt, nie als Zufallsquelle.
 */
export function streue(text: string): () => number {
  let h = 1779033703 ^ text.length;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/** sfc32 — der eigentliche Generator. */
function sfc32(zustand: [number, number, number, number]): () => number {
  return () => {
    let [a, b, c, d] = zustand;
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    zustand[0] = a >>> 0;
    zustand[1] = b >>> 0;
    zustand[2] = c >>> 0;
    zustand[3] = d >>> 0;
    return (t >>> 0) / 4294967296;
  };
}

class StromImpl implements Strom {
  readonly name: string;
  #zustand: [number, number, number, number];
  #naechste: () => number;
  #gezogen = 0;

  constructor(name: string, start: StromZustand) {
    this.name = name;
    this.#zustand = [start[0] >>> 0, start[1] >>> 0, start[2] >>> 0, start[3] >>> 0];
    this.#naechste = sfc32(this.#zustand);
    // Vorlauf: sfc32 braucht ein paar Runden, bis sich der Startwert gut verteilt hat.
    for (let i = 0; i < 12; i++) this.#naechste();
  }

  zahl(): number {
    this.#gezogen++;
    return this.#naechste();
  }

  ganz(min: number, max: number): number {
    if (max < min) throw new Error(`ganz(${min}, ${max}): max < min`);
    return min + Math.floor(this.zahl() * (max - min + 1));
  }

  spanne(min: number, max: number): number {
    return min + this.zahl() * (max - min);
  }

  chance(p: number): boolean {
    if (p <= 0) return false;
    if (p >= 1) {
      // Auch die sichere Chance zieht — sonst verschiebt eine Balance-Aenderung,
      // die ein p auf 1 setzt, den ganzen nachfolgenden Strom.
      this.zahl();
      return true;
    }
    return this.zahl() < p;
  }

  waehle<T>(liste: readonly T[]): T {
    if (liste.length === 0) throw new Error('waehle(): leere Liste');
    const wert = liste[this.ganz(0, liste.length - 1)];
    // noUncheckedIndexedAccess: der Index ist durch ganz() nachweislich gueltig.
    return wert as T;
  }

  gewichtet<T>(liste: readonly T[], gewichte: readonly number[]): T {
    if (liste.length === 0) throw new Error('gewichtet(): leere Liste');
    if (liste.length !== gewichte.length) {
      throw new Error(`gewichtet(): ${liste.length} Elemente, aber ${gewichte.length} Gewichte`);
    }
    let summe = 0;
    for (const g of gewichte) {
      if (g < 0 || !Number.isFinite(g)) throw new Error(`gewichtet(): ungueltiges Gewicht ${g}`);
      summe += g;
    }
    if (summe <= 0) throw new Error('gewichtet(): Summe der Gewichte ist 0');

    let wurf = this.zahl() * summe;
    for (let i = 0; i < liste.length; i++) {
      wurf -= gewichte[i] as number;
      if (wurf < 0) return liste[i] as T;
    }
    // Nur durch Gleitkomma-Rundung erreichbar.
    return liste[liste.length - 1] as T;
  }

  mische<T>(liste: readonly T[]): T[] {
    const kopie = liste.slice();
    for (let i = kopie.length - 1; i > 0; i--) {
      const j = this.ganz(0, i);
      const h = kopie[i] as T;
      kopie[i] = kopie[j] as T;
      kopie[j] = h;
    }
    return kopie;
  }

  zustand(): StromZustand {
    return [this.#zustand[0], this.#zustand[1], this.#zustand[2], this.#zustand[3]];
  }

  setzeZustand(z: StromZustand): void {
    this.#zustand[0] = z[0] >>> 0;
    this.#zustand[1] = z[1] >>> 0;
    this.#zustand[2] = z[2] >>> 0;
    this.#zustand[3] = z[3] >>> 0;
  }

  zaehler(): number {
    return this.#gezogen;
  }
}

/**
 * Die kanonischen Ströme. Neue Ströme hier ergänzen, nie ad hoc erzeugen —
 * sonst weiß niemand mehr, welcher Wurf woher kommt.
 *
 * `sichtbar` ist bewusst getrennt: Grafikeffekte dürfen den Spielzustand nie
 * beeinflussen, also ziehen sie aus einem eigenen Strom.
 */
export const STROEME = [
  'welt',      // Aufbau eines Abstiegs: Räume, Wege, Wahrzeichen
  'spawn',     // welche Gegner wo erscheinen
  'kampf',     // Trefferwürfe, Ausweichen, Gegnerentscheidungen
  'kritisch',  // kritische Treffer
  'beute',     // ob und welche Kategorie fällt
  'affix',     // welche Affixe ein Gegenstand bekommt
  'zahl',      // Werte innerhalb der Affix-Spannen
  'sichtbar',  // rein kosmetisch — beeinflusst NIE den Spielzustand
] as const;

export type StromName = (typeof STROEME)[number];

export type StroemeZustand = Partial<Record<StromName, StromZustand>>;

export class Zufall {
  readonly seed: string;
  readonly #stroeme = new Map<StromName, StromImpl>();

  constructor(seed: string, zustand?: StroemeZustand) {
    this.seed = seed;
    for (const name of STROEME) {
      const gespeichert = zustand?.[name];
      this.#stroeme.set(name, new StromImpl(name, gespeichert ?? startwert(seed, name)));
    }
  }

  strom(name: StromName): Strom {
    const s = this.#stroeme.get(name);
    if (!s) throw new Error(`Unbekannter Strom: ${name}`);
    return s;
  }

  /** Vollständiger Zustand aller Ströme — gehört in jeden Spielstand. */
  zustand(): StroemeZustand {
    const aus: StroemeZustand = {};
    for (const [name, strom] of this.#stroeme) aus[name] = strom.zustand();
    return aus;
  }

  setzeZustand(z: StroemeZustand): void {
    for (const [name, strom] of this.#stroeme) {
      const s = z[name];
      if (s) strom.setzeZustand(s);
    }
  }

  /** Diagnose: wie oft wurde aus welchem Strom gezogen. */
  zaehler(): Record<string, number> {
    const aus: Record<string, number> = {};
    for (const [name, strom] of this.#stroeme) aus[name] = strom.zaehler();
    return aus;
  }
}

/** Startwert eines Stroms aus Seed und Stromnamen. */
export function startwert(seed: string, strom: string): StromZustand {
  const h = streue(`${seed}::${strom}`);
  return [h(), h(), h(), h()];
}

/**
 * Kleiner, stabiler Hash über beliebige Zahlenfolgen.
 * Wird für die Signatur goldener Läufe benutzt — nicht kryptografisch,
 * aber empfindlich genug, um jede Abweichung zu bemerken.
 */
export function signatur(werte: Iterable<number>): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (const w of werte) {
    // Gleitkommazahlen ueber ihre Bitdarstellung einbeziehen, damit auch
    // winzige Abweichungen auffallen.
    const puffer = new DataView(new ArrayBuffer(8));
    puffer.setFloat64(0, w);
    const a = puffer.getUint32(0);
    const b = puffer.getUint32(4);
    h1 = Math.imul(h1 ^ a, 2654435761);
    h2 = Math.imul(h2 ^ b, 1597334677);
    h1 = (h1 << 13) | (h1 >>> 19);
    h2 = (h2 << 7) | (h2 >>> 25);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h2 = Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  return ((h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0'));
}
